/**
 * Measured lengths (#155) — the read-only side of the ruler.
 *
 * The panel answers "how big is this thing" for whatever is selected, which is
 * the question you'd otherwise reach for the ruler to answer approximately.
 * The ruler tool answers "how far is it from here to there" for two points
 * that need not belong to anything.
 */
import type paper from "paper";
import type { StitchLine } from "./Project";
import { stitchLineSegments } from "./seamGeometry";

/** Total arc length of every subpath in `pathData`, in project mm. Returns 0
 *  for anything unparseable rather than throwing — a readout is not worth
 *  taking a panel down for. */
export function pathLengthMm(
  pathData: string,
  scope: paper.PaperScope,
): number {
  if (!pathData) return 0;
  let cp: paper.CompoundPath;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return 0;
  }
  const subs: paper.Path[] =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  let total = 0;
  for (const sub of subs) total += sub.length ?? 0;
  cp.remove();
  return total;
}

/** A seam's length along its own curve — the distance actually stitched,
 *  which is what the arc-length walker the holes are laid out by measures. */
export function seamLengthMm(line: StitchLine): number {
  return stitchLineSegments(line).total;
}

/** Format a measured length for a panel readout, matching the canvas
 *  overlay's units so the two can be compared without conversion. */
export function formatLengthMm(mm: number, units: "mm" | "in"): string {
  return units === "in" ? `${(mm / 25.4).toFixed(3)} in` : `${mm.toFixed(1)} mm`;
}

/**
 * Enclosed area of `pathData`, in square mm (#1056).
 *
 * Subpaths are summed by SIGNED area, so a compound path's holes subtract:
 * a part with a cut-out reads as the leather you actually use, not as the
 * outline it was cut from. `Math.abs` at the end because a subpath's sign
 * depends on its winding direction, which is not something the user chose.
 *
 * Returns 0 for anything unparseable rather than throwing — a readout is not
 * worth taking a panel down for.
 */
export function pathAreaMm2(pathData: string, scope: paper.PaperScope): number {
  if (!pathData) return 0;
  let cp: paper.CompoundPath;
  try {
    cp = new scope.CompoundPath({ pathData, insert: false });
  } catch {
    return 0;
  }
  const subs: paper.Path[] =
    cp.children && cp.children.length > 0
      ? (cp.children as paper.Path[])
      : [cp as unknown as paper.Path];
  let total = 0;
  for (const sub of subs) total += sub.area ?? 0;
  cp.remove();
  return Math.abs(total);
}

/** The area of `pathData`'s bounding box, in square mm — how much sheet the
 *  shape occupies rather than how much of it is leather. The two together are
 *  what say whether a part nests well. */
export function pathBoundsAreaMm2(
  pathData: string,
  scope: paper.PaperScope,
): number {
  if (!pathData) return 0;
  try {
    const cp = new scope.CompoundPath({ pathData, insert: false });
    const b = cp.bounds;
    const area = (b?.width ?? 0) * (b?.height ?? 0);
    cp.remove();
    return area;
  } catch {
    return 0;
  }
}

const MM2_PER_IN2 = 25.4 * 25.4;
/** 1 ft² in in² — where an imperial readout stops being legible in inches. */
const IN2_PER_FT2 = 144;
/** 1 dm² in mm²: leather is bought by the square decimetre, so that is the
 *  unit a maker already thinks in. */
const MM2_PER_DM2 = 100 * 100;
/** 1 m² in dm². */
const DM2_PER_M2 = 100;

/** Every area number is shown to this many decimals — both units, at every
 *  magnitude. A readout that changed its precision with the value is harder to
 *  scan down a panel than one that doesn't. */
const AREA_DECIMALS = 2;

/**
 * Format a measured area for a panel readout (#1056).
 *
 * BOTH units of the system are shown, the trade unit first and the larger one
 * beside it — "0.70 dm² (0.01 m²)". The units are the ones the trade uses
 * rather than the strict SI / imperial siblings of the length units: leather is
 * sold by the **square decimetre** in metric markets and by the **square foot**
 * in imperial ones, so a maker can price a part against a hide without
 * converting anything. The larger unit is what a supplier's hide listing or a
 * workshop's floor space is quoted in, so having it alongside means neither
 * comparison needs arithmetic.
 *
 * An earlier cut stepped UP to the larger unit past a threshold instead of
 * showing both, which meant a maker only ever saw one of the two numbers they
 * wanted and, for anything smaller than a square metre, never saw the larger
 * unit at all.
 *
 * Known consequence of the fixed two decimals, accepted deliberately: a small
 * part reads "0.00" in the LARGER unit — a 40 × 10 mm strap keeper is 0.0004 m²
 * and 0.0028 ft². The trade unit, which is the one being read at that size,
 * still carries the number.
 */
export function formatAreaMm2(mm2: number, units: "mm" | "in"): string {
  if (units === "in") {
    const in2 = mm2 / MM2_PER_IN2;
    const ft2 = in2 / IN2_PER_FT2;
    return `${in2.toFixed(AREA_DECIMALS)} in² (${ft2.toFixed(
      AREA_DECIMALS,
    )} ft²)`;
  }
  const dm2 = mm2 / MM2_PER_DM2;
  const m2 = dm2 / DM2_PER_M2;
  return `${dm2.toFixed(AREA_DECIMALS)} dm² (${m2.toFixed(AREA_DECIMALS)} m²)`;
}
